import * as React from "react";

interface Args extends IntersectionObserverInit {
  freezeOnceVisible?: boolean;
}

export const useIntersectionObserver = ({
  threshold = 0,
  root = null,
  rootMargin = "0%",
  freezeOnceVisible = false,
}: Args = {}): [
  React.RefCallback<HTMLElement>,
  IntersectionObserverEntry | undefined
] => {
  const ref = React.useRef<Element | null>();
  const [entry, setEntry] = React.useState<IntersectionObserverEntry>();
  const [observer, setObserver] = React.useState<IntersectionObserver | null>(
    null
  );

  const frozen = entry?.isIntersecting && freezeOnceVisible;

  const updateEntry = ([entry]: IntersectionObserverEntry[]): void => {
    setEntry(entry);
  };

  const attach = (node: HTMLElement) => {
    const observerParams = { threshold, root, rootMargin };
    const newObserver = new IntersectionObserver(updateEntry, observerParams);

    newObserver.observe(node);

    setObserver(newObserver);
  };

  const detach = () => {
    if (observer) {
      observer.disconnect();
    }
  };

  const setRef: React.RefCallback<HTMLElement> = React.useCallback((node) => {
    detach();

    const hasIOSupport = !!window.IntersectionObserver;
    if (hasIOSupport && !frozen && node) {
      attach(node);
    }

    ref.current = node;
  }, []);

  return [setRef, entry];
};
